import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Card, Button, Alert, LoadingState } from '../ui';
import projectService from '../../services/projectService';
import KeywordGenerationAgent from './KeywordGenerationAgent';
import { ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';

const parseList = (value, prefix = '') => String(value || '')
  .split(/[\n,]+/)
  .map((item) => item.trim().toLowerCase())
  .filter(Boolean)
  .map((item) => (prefix && !item.startsWith(prefix) ? `${prefix}${item}` : item));

const listToText = (value) => Array.isArray(value) ? value.join('\n') : '';

const stripPrefix = (kw) => (typeof kw === 'string' && (kw.startsWith('+') || kw.startsWith('-'))
  ? kw.substring(1)
  : kw);

const buildKeywordSummary = (category) => {
  const primary = (category.primary_keywords || []);
  const secondary = (category.secondary_keywords || []).map(stripPrefix);
  const minus = (category.minus_words || []).map(stripPrefix);
  const lines = [
    `Category: ${category.name}`,
    '',
    primary.length > 0
      ? `Primary keywords (${primary.length}) — these are the main terms that trigger a match:`
      : 'Primary keywords: none',
    ...(primary.length > 0 ? primary : []),
    '',
    secondary.length > 0
      ? `Secondary keywords (${secondary.length}) — message must ALSO contain at least one of these:`
      : 'Secondary keywords: none',
    ...(secondary.length > 0 ? secondary : []),
    '',
    minus.length > 0
      ? `Minus words (${minus.length}) — if any of these are present, the message is ignored:`
      : 'Minus words: none',
    ...(minus.length > 0 ? minus : []),
  ];
  return lines.join('\n');
};

const emptyForm = {
  name: '',
  primary_keywords: '',
  secondary_keywords: '',
  minus_words: '',
};

function CategoryEditor({ project, category, onSaved, onDeleted }) {
  const [form, setForm] = useState({
    name: category.name || '',
    primary_keywords: listToText(category.primary_keywords),
    secondary_keywords: listToText(category.secondary_keywords),
    minus_words: listToText(category.minus_words),
    is_active: category.is_active !== false,
  });
  const [copied, setCopied] = useState(false);
  const [localWarning, setLocalWarning] = useState('');

  const updateMutation = useMutation((payload) => (
    projectService.updateProjectCategory(project.id, category.id, payload)
  ), {
    onSuccess: (result) => {
      setLocalWarning(result?.warning?.message || '');
      onSaved();
    },
    onError: () => setLocalWarning(''),
  });

  const deleteMutation = useMutation(() => (
    projectService.deleteProjectCategory(project.id, category.id)
  ), { onSuccess: onDeleted });

  const topicUrl = project.report_group_id && category.report_topic_id
    ? `https://t.me/c/${String(project.report_group_id).replace('-100', '')}/${category.report_topic_id}`
    : null;

  const save = () => {
    setLocalWarning('');
    updateMutation.mutate({
      name: form.name,
      primary_keywords: parseList(form.primary_keywords),
      secondary_keywords: parseList(form.secondary_keywords, '+'),
      minus_words: parseList(form.minus_words, '-'),
      is_active: form.is_active,
      create_topic: true,
    });
  };

  const copyKeywords = async () => {
    const text = buildKeywordSummary(category);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      // fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            placeholder="Category name"
          />
          <div className="mt-1 text-xs text-gray-500">
            {category.report_topic_id ? `Topic #${category.report_topic_id}` : 'No topic yet — Save will create it if bot/group are ready'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {topicUrl && (
            <a
              href={topicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs rounded border border-primary-200 bg-primary-50 px-2 py-1 text-primary-700 hover:bg-primary-100"
            >
              Open topic
            </a>
          )}
          <label className="inline-flex items-center gap-1 text-xs text-gray-600">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            active
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Primary keywords</label>
          <textarea
            rows={4}
            value={form.primary_keywords}
            onChange={(e) => setForm({ ...form, primary_keywords: e.target.value })}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            placeholder="one per line or comma"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Secondary +keywords</label>
          <textarea
            rows={4}
            value={form.secondary_keywords}
            onChange={(e) => setForm({ ...form, secondary_keywords: e.target.value })}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            placeholder="+context"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Minus -words</label>
          <textarea
            rows={4}
            value={form.minus_words}
            onChange={(e) => setForm({ ...form, minus_words: e.target.value })}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            placeholder="-spam"
          />
        </div>
      </div>

      {localWarning && (
        <Alert variant="warning" className="mt-3">
          {localWarning}
        </Alert>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={copyKeywords}
          title="Copy a human-readable summary of all keywords in this category"
        >
          {copied ? (
            <>
              <CheckIcon className="h-4 w-4 mr-1 inline" />
              Copied!
            </>
          ) : (
            <>
              <ClipboardDocumentIcon className="h-4 w-4 mr-1 inline" />
              Copy keywords
            </>
          )}
        </Button>
        <Button
          type="button"
          variant="danger"
          onClick={() => {
            if (window.confirm(`Delete category "${category.name}"?`)) {
              deleteMutation.mutate();
            }
          }}
          isLoading={deleteMutation.isLoading}
        >
          Delete
        </Button>
        <Button type="button" variant="primary" onClick={save} isLoading={updateMutation.isLoading}>
          Save
        </Button>
      </div>
    </div>
  );
}

const KeywordCategoriesManager = ({ project, isNewProject }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(emptyForm);
  const [localError, setLocalError] = useState('');

  const categoriesQuery = useQuery(
    ['project-categories', project?.id],
    () => projectService.getProjectCategories(project.id),
    { enabled: Boolean(project?.id && !isNewProject) }
  );

  const createMutation = useMutation((payload) => (
    projectService.createProjectCategory(project.id, payload)
  ), {
    onSuccess: () => {
      setForm(emptyForm);
      setLocalError('');
      queryClient.invalidateQueries(['project-test-run', project.id]);
      categoriesQuery.refetch();
    },
    onError: (err) => setLocalError(err?.response?.data?.detail || err?.response?.data?.error || err.message),
  });

  if (isNewProject) {
    return (
      <Card className="mt-4">
        <h2 className="text-lg font-medium text-gray-900 mb-2">Keyword Categories</h2>
        <p className="text-sm text-gray-600">Save or auto-provision the project first, then add keyword categories/topics.</p>
      </Card>
    );
  }

  if (categoriesQuery.isLoading) {
    return <LoadingState message="Loading categories..." />;
  }

  const categories = categoriesQuery.data?.data || [];

  const create = () => {
    if (!form.name.trim()) {
      setLocalError('Category name is required');
      return;
    }
    createMutation.mutate({
      name: form.name,
      primary_keywords: parseList(form.primary_keywords),
      secondary_keywords: parseList(form.secondary_keywords, '+'),
      minus_words: parseList(form.minus_words, '-'),
      create_topic: true,
    });
  };

  return (
    <Card className="mt-4">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Keyword Categories</h2>
          <p className="text-sm text-gray-600">
            Each category has its own keyword set and routes matching messages to its Telegram forum topic.
          </p>
        </div>
        {project.invite_link && (
          <a
            href={project.invite_link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs rounded border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700 hover:bg-gray-100"
          >
            Join forum
          </a>
        )}
      </div>

      {(localError || categoriesQuery.error) && (
        <Alert variant="error" className="mb-4">
          {localError || categoriesQuery.error.message}
        </Alert>
      )}

      <div className="space-y-4 mb-6">
        {categories.length === 0 ? (
          <div className="rounded-md bg-yellow-50 border border-yellow-200 p-3 text-sm text-yellow-800">
            No categories yet. Add the first category below — it will create a Telegram topic automatically when possible.
          </div>
        ) : categories.map((category) => (
          <CategoryEditor
            key={category.id}
            project={project}
            category={category}
            onSaved={() => {
              queryClient.invalidateQueries(['project-test-run', project.id]);
              categoriesQuery.refetch();
            }}
            onDeleted={() => {
              queryClient.invalidateQueries(['project-test-run', project.id]);
              categoriesQuery.refetch();
            }}
          />
        ))}
      </div>

      <KeywordGenerationAgent
        project={project}
        onApply={(generatedForm) => {
          setForm(generatedForm);
          setLocalError('');
        }}
      />

      <div className="border-t border-gray-200 pt-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Add category</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g. Competitors"
          />
          <textarea
            rows={3}
            value={form.primary_keywords}
            onChange={(e) => setForm({ ...form, primary_keywords: e.target.value })}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            placeholder="primary keywords"
          />
          <textarea
            rows={3}
            value={form.secondary_keywords}
            onChange={(e) => setForm({ ...form, secondary_keywords: e.target.value })}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            placeholder="secondary +keywords"
          />
          <textarea
            rows={3}
            value={form.minus_words}
            onChange={(e) => setForm({ ...form, minus_words: e.target.value })}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            placeholder="minus -words"
          />
        </div>
        <div className="mt-3 flex justify-end">
          <Button type="button" variant="primary" onClick={create} isLoading={createMutation.isLoading}>
            Add category + topic
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default KeywordCategoriesManager;
